import { BusinessType, DataType } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { AppDataType, TypedValue } from "src/types/typedValue";

export const parseTypedValue = (value: string, type: AppDataType): TypedValue => {
  switch (type) {
    case "date":
      return { type, value: new Date(value) };
    case "amount":
      return { type, value: parseFloat(value) };
    case "percentage":
      return { type, value: parseFloat(value) };
    case "float":
      return { type, value: parseFloat(value) };
    case "integer":
      return { type, value: parseInt(value) };
    case "string":
      return { type, value };
    case "boolean":
      return { type, value: value === "true" };
    default:
      return {
        type: "string",
        value,
      };
  }
};

const BUSINESS_TYPE_TO_APP_DATA_TYPE_MAP: { [key in BusinessType]: AppDataType } = {
  FullAddress: "string",
  IBAN: "string",
  Phone: "string",
  SIREN: "string",
  SIRET: "string",
  DateTime: "dateTime",
  Date: "date",
  Email: "string",
  Ratio: "percentage",
  Amount: "amount",
  Float: "float",
  Timestamp: "date",
  String: "string",
  Boolean: "boolean",
  Integer: "integer",
  Code: "code",
  Codes: "codes",
  GPS: "gps",
  StringList: "stringList",
  StringMap: "stringMap",
  VertexId: "vertexId",
  VertexIdList: "vertexIdList",
};

const APP_DATA_TYPE_TO_BUSINESS_TYPE_MAP = Object.entries(
  BUSINESS_TYPE_TO_APP_DATA_TYPE_MAP
).reduce((reverseMap, [businessType, appDataType]) => {
  reverseMap[appDataType] = businessType as BusinessType;
  return reverseMap;
}, {} as { [key in AppDataType]: BusinessType });

export const mapBusinessTypeToAppDataType = (businessType: BusinessType): AppDataType => {
  return BUSINESS_TYPE_TO_APP_DATA_TYPE_MAP[businessType];
};

const DATA_TYPE_TO_APP_DATA_TYPE_MAP: { [key in DataType]: AppDataType } = {
  String: "string",
  Boolean: "boolean",
  Integer: "integer",
  Float: "float",
  Code: "code",
  Codes: "codes",
  GPS: "gps",
  StringList: "stringList",
  StringMap: "stringMap",
  VertexId: "vertexId",
  VertexIdList: "vertexIdList",
  Timestamp: "date",
};

const APP_DATA_TYPE_TO_DATA_TYPE_MAP = Object.entries(DATA_TYPE_TO_APP_DATA_TYPE_MAP).reduce(
  (reverseMap, [dataType, appDataType]) => {
    reverseMap[appDataType] = dataType as DataType;
    return reverseMap;
  },
  {} as Partial<{ [key in AppDataType]: DataType }>
);

export const mapDataTypeToAppDataType = (dataType: DataType): AppDataType => {
  return DATA_TYPE_TO_APP_DATA_TYPE_MAP[dataType];
};

export const mapToBusinessType = (appDataType: AppDataType): BusinessType => {
  return APP_DATA_TYPE_TO_BUSINESS_TYPE_MAP[appDataType];
};

const ADDITIONAL_APP_DATA_TYPE_TO_DATA_TYPE_MAP: { [key in AppDataType]?: DataType } = {
  dateTime: "Timestamp", // Both date and dateTime map to Timestamp
  amount: "Float", // No direct DataType for amount, use Float
  percentage: "Float", // No direct DataType for percentage, use Float
};

export const mapToApiDataType = (appDataType: AppDataType): DataType => {
  const dataType = APP_DATA_TYPE_TO_DATA_TYPE_MAP[appDataType];
  if (dataType) {
    return dataType;
  }

  const additionalDataType = ADDITIONAL_APP_DATA_TYPE_TO_DATA_TYPE_MAP[appDataType];
  if (additionalDataType) {
    return additionalDataType;
  }

  return "String";
};
